<template>
  <div class="mt-3">
    <div class="d-flex flex-wrap justify-between items-center">
      <p class="title">{{ $t('views.configurator.color') }}</p>
    </div>
    <div class="p-1">
      <el-input :placeholder="$t('views.configurator.searchColor')" v-model="searchColorQuery"/>
    </div>
    <div class="view-wrapper mt-2" ref="colorScrollContainer">
      <el-scrollbar>
        <div class="scroll-selection-container">
          <mesh-color-selector
            :mesh-colors="filteredMeshColors(meshColors, searchColorQuery)"
            :mesh-texture-colors="meshTextureColors"
            :mesh-embossing-colors="meshEmbossingColors"
            :selected-product-name="selectedProductName"
            :selected-color="selectedColor"
            :selected-engraving="selectedEngraving"
            @mesh-color-selected="handleMeshColorSelected"
          />
        </div>
      </el-scrollbar>
    </div>

    <div v-if="!isSCover && !isXCoverBack && this.meshEngravings.length > 0" class="d-flex flex-wrap justify-between items-center">
      <p class="title mt-4">{{ $t('views.configurator.engraving') }}</p>
    </div>

    <!-- Single MeshColorSelector component with conditional props -->
    <div class="p-1" v-show="!isSCover && !isXCoverBack && this.meshEngravings.length > 0">
      <el-input :placeholder="$t('views.configurator.searchEngraving')" v-model="searchEngravingQuery"/>
    </div>
    <div v-if="!isSCover && !isXCoverBack && this.meshEngravings.length > 0" class="view-wrapper mt-2" ref="designScrollContainer">
      <el-scrollbar>
        <div class="scroll-selection-container">
          <mesh-design-selector :mesh-engravings="filteredMeshEngravings(meshEngravings, searchEngravingQuery)" :selected-engraving="selectedEngraving" @mesh-design-selected="handleMeshEngravingSelected" />
        </div>
      </el-scrollbar>
    </div>

    <div v-if="!isSCover && isEngravingColorSelection" class="d-flex flex-wrap justify-between items-center">
      <p class="title mt-4">{{ $t('views.configurator.engravingColor') }}</p>
    </div>
    <div class="p-1" v-show="!isSCover && isEngravingColorSelection">
      <el-input :placeholder="$t('views.configurator.searchEngravingColor')" v-model="searchEngravingColorQuery"/>
    </div>
    <div v-if="!isSCover && isEngravingColorSelection" class="view-wrapper mt-2" ref="designColorScrollContainer">
      <el-scrollbar>
        <div class="scroll-selection-container" style="height: fit-content">
          <mesh-design-color-selector
            :mesh-colors="meshColors"
            :mesh-texture-colors="meshTextureColors"
            :mesh-embossing-colors="filteredMeshColors(meshEmbossingColors, searchEngravingColorQuery)"
            :selected-product-name="selectedProductName"
            :selected-color="selectedColor"
            :selected-engraving="selectedEngraving"
            :selected-engraving-color="selectedEmbossingColor"
            @mesh-embossing-color-selected="handleEmbossingColorSelected"
            @mesh-texture-color-selected="handleTextureColorSelected"
          />
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import MeshColorSelector from './MeshColorsSelector.vue';
import MeshDesignSelector from './MeshDesignsSelector.vue';
import { SupportedValues } from '@/types/api/Utility/SupportedValues';
import { RGB2 } from '@/types/api/Utility/RGB2';
import MeshDesignColorSelector from './MeshDesignColorSelector.vue';
import { MeshTreeData } from '@/types/ui/MeshTreeData';
import { useComponentSettingStore } from '@/utils/componentSettings';

@Component({
  components: {
    MeshColorSelector,
    MeshDesignSelector,
    MeshDesignColorSelector,
  },
  emits: ['colorizeSelectedMesh', 'colorizeEmbossingMesh', 'colorizeTextureMesh', 'handleSelectEmbossing'],
})
@Component
export default class MeshColorsAndDesign extends Vue {
  @Prop({ default: null }) selectedProductName!: string | null;
  @Prop({ default: [] }) meshEngravings!: SupportedValues[];
  @Prop({ default: [] }) meshTextureColors!: RGB2[];
  @Prop({ default: [] }) meshEmbossingColors!: RGB2[];
  @Prop({ default: [] }) meshColors!: RGB2[];

  @Prop() selectedEmbossingColor!: RGB2;
  @Prop() selectedColor!: RGB2;
  @Prop() selectedTextureColor!: RGB2;
  @Prop() selectedEngraving!: number;
  @Prop() selectedTexture!: number;

  @Prop() meshTreeData!: MeshTreeData;

  settingStore = useComponentSettingStore();
  isEngravingColorSelection = false;
  private lastScrollPositions: Record<string, number> = {
    color: 0,
    design: 0,
    designColor: 0,
  };

  searchColorQuery = ''
  searchEngravingQuery = ''
  searchEngravingColorQuery = ''

  colorContainer: HTMLElement | null = null;
  designContainer: HTMLElement | null = null;
  designColorContainer: HTMLElement | null = null;

  get isSCover(): boolean {
    return this.selectedProductName?.toLowerCase().includes('s-cover') || false;
  }

  get isXCoverBack(): boolean {
    const selected = this.meshTreeData?.getSelection?.()[0];
    const treeName = selected?.name?.toLowerCase() ?? '';
    const productName = this.selectedProductName?.toLowerCase() ?? '';
    return productName.includes('x-cover') && treeName.includes('hinten');
  }

  mounted() {
    this.updateScrollListeners();
    this.restoreScrollPositions();
    this.handleEngravingColorSelection(this.selectedEngraving);

    if(this.settingStore.getAllComponentSettings().length === 0) {
      this.handleMeshColorSelected(this.selectedColor);
      this.settingStore.saveConfig({ color: this.selectedColor.name });
      this.settingStore.saveConfig({ embossing: this.meshEngravings[0].thumbnail?.name }); 
    }
  }

  updated() {
    this.updateScrollListeners();
    this.restoreScrollPositions();
  }

  updateScrollListeners() {
    this.colorContainer = (this.$refs.colorScrollContainer as HTMLElement) || null;
    this.designContainer = (this.$refs.designScrollContainer as HTMLElement) || null;
    this.designColorContainer = (this.$refs.designColorScrollContainer as HTMLElement) || null;
  }

  private storeScrollPositions() {
    if (this.colorContainer) {
      const scrollElement = this.colorContainer.firstElementChild?.firstElementChild
      this.lastScrollPositions.color = scrollElement?.scrollTop ?? 0
    }
    if (this.designContainer) {
      const scrollElement = this.designContainer.firstElementChild?.firstElementChild
      this.lastScrollPositions.design = scrollElement?.scrollTop ?? 0
    }
    if (this.designColorContainer) {
      const scrollElement = this.designColorContainer.firstElementChild?.firstElementChild
      this.lastScrollPositions.designColor = scrollElement?.scrollTop ?? 0
    }
  }

  private restoreScrollPositions() {
    this.$nextTick(() => {
      if (this.colorContainer) {
        const scrollElement = this.colorContainer.firstElementChild?.firstElementChild
        if (scrollElement) {
          scrollElement.scrollTop = this.lastScrollPositions.color;
        }
      }
      if (this.designContainer){
        const scrollElement = this.designContainer.firstElementChild?.firstElementChild
        if (scrollElement) {
          scrollElement.scrollTop = this.lastScrollPositions.design
        }
      }
      if (this.designColorContainer){
          const scrollElement = this.designColorContainer.firstElementChild?.firstElementChild
        if (scrollElement) {
          scrollElement.scrollTop = this.lastScrollPositions.designColor
        }
      }
    });
  }

  handleMeshColorSelected(meshColor: RGB2) {
    this.storeScrollPositions();
    this.$emit('colorizeSelectedMesh', meshColor);
    this.restoreScrollPositions();

    this.settingStore.saveConfig({ color: meshColor.name })
  }

  handleEngravingColorSelection(design: number) {
    this.storeScrollPositions();
    if (
      this.selectedProductName?.toLowerCase().includes('x-cover') &&
      this.meshTreeData.getSelection()[0].name.toLowerCase().includes('vorne') &&
      [878, 885, 886, 893].includes(design)
    ) {
      this.isEngravingColorSelection = true;
    } else {
      this.isEngravingColorSelection = false;
      if (this.meshEmbossingColors.length > 0 && this.selectedEmbossingColor && this.selectedEmbossingColor !== this.meshEmbossingColors[0]) {
        this.handleEmbossingColorSelected(this.meshEmbossingColors[0]);
      }
    }

    this.$forceUpdate();
    this.restoreScrollPositions();
  }

  handleEmbossingColorSelected(embossingColor: RGB2) {
    this.storeScrollPositions();
    this.$emit('colorizeEmbossingMesh', embossingColor);
    this.restoreScrollPositions();

    this.settingStore.saveConfig({ embossingColor: embossingColor.name })
  }

  handleTextureColorSelected(textureColor: RGB2) {
    this.storeScrollPositions();
    this.$emit('colorizeTextureMesh', textureColor);
    this.restoreScrollPositions();
  }

  handleMeshEngravingSelected(design: SupportedValues) {
    this.storeScrollPositions();
    this.$emit('handleSelectEmbossing', design.fileTextureId);

    this.handleEngravingColorSelection(design.fileId!);
    this.restoreScrollPositions();
  }

  filteredMeshColors(colors:RGB2[], query:string){
    if(!colors) return colors
    return colors.filter((color) => color.name.toLowerCase().includes(query.toLowerCase()) || this.$t(`views.colors.${color.name}`)?.toLowerCase().includes(query.toLowerCase()))
  }

  filteredMeshEngravings(engravings:SupportedValues[], query:string){
    return engravings.filter((engraving) => engraving.thumbnail?.name?.toLowerCase()?.includes(query.toLowerCase()) || this.$t(`views.designs.${engraving.thumbnail?.name}`)?.toLowerCase().includes(query.toLowerCase()))
  }
}
</script>

<style lang="scss" scoped>
.scroll-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  position: relative;
}

.view-wrapper::-webkit-scrollbar {
  display: none;
}

.view-wrapper {
  display: flex;
  align-items: flex-start;
  justify-self: center;
  width: 100%;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: thin;
}

.scroll-selection-container {
  display: flex;
  flex-wrap: wrap;
  max-height: 18.5rem;
  width: 100%;
  gap: 0.5rem;
  padding: 0.2rem;
  align-content: start;
}

@media (min-width: 768px) {
  .view-selection-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
}

@media (max-width: 768px) {
  .view-selection-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
}

.view-selection-container > * {
  flex: 0 0 auto;
}

.d-flex {
  display: flex;
}

.justify-between {
  justify-content: space-between;
}

.items-center {
  align-items: center;
}

.title {
  margin-top: auto;
  padding-left: 0.2rem;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #1d1e19;
}

.view-all {
  margin-top: auto;
  margin-bottom: 7px;
  font-weight: 700;
  font-size: 14px;
  color: #5258e4;
  text-decoration: none;
}
</style>
